import { Box, Typography, Link, Divider } from "@mui/material";
import CustomUploadFileV2 from "@components/CustomUploadFileV2";
import { BofuTabCode, BofuTabDocument, DocumentResponse } from "@interfaces/response";
import React from "react";

interface InputUploadDocumentProps {
  inputPrefix?: string;
  docItem: BofuTabDocument;
  onChange?: (params: {
    newValue: DocumentResponse | undefined;
    document: BofuTabDocument;
    subAction?: ValueAction;
  }) => void;
  onChangeMultiple?: (params: {
    newValue: DocumentResponse | undefined;
    document: BofuTabDocument;
    valueAction?: ValueAction;
    valueIndex?: number;
  }) => void;
  documentIndex: number;
  uploadService?: "default" | "second_guarantee";
  RFC?: string;
}

export type ValueAction = "add" | "remove" | "update";

const UploadMultipleDocument = ({
  inputPrefix,
  docItem,
  onChangeMultiple,
  documentIndex,
  uploadService,
  RFC,
}: InputUploadDocumentProps) => {
  const { list_values } = docItem;
  return (
    <Box>
      {docItem?.list_values?.map((valueItem, valueIndex) => (
        <React.Fragment key={`${inputPrefix}_multiple_document_${documentIndex}_${valueIndex}`}>
          <CustomUploadFileV2
            py={2}
            inputName={`${inputPrefix || ""}_multiple_document_${documentIndex}_${valueIndex}`}
            optionalName={docItem.name}
            optionalDescription={docItem.label || ""}
            disabled={docItem.disabled}
            uploadedFile={valueItem}
            multiple={!valueItem}
            setUploadedFile={(newValue, options) => {
              const isAdditionalFile = Boolean(newValue) && !valueItem && (options?.fileIndex ?? 0) > 0;

              onChangeMultiple?.({
                document: docItem,
                newValue,
                valueAction: isAdditionalFile ? "add" : "update",
                valueIndex: isAdditionalFile ? undefined : valueIndex,
              });
            }}
            metadata={{
              fileCode: docItem.file_category_code,
              uploadType: docItem.upload_type || "client",
              clientDetailsId: docItem.client_details_id,
              personId: docItem.person_id,
              guaranteeId: docItem.guarantee_id,
            }}
            required={docItem.required}
            uploadService={uploadService || "default"}
            hiddeDivider={true}
            validation_type={docItem.validation_type}
            specific_validations={docItem.specific_validations}
            RFC={RFC}
          />
        </React.Fragment>
      ))}

      <Box textAlign="right" my={2}>
        <Link
          onClick={() => {
            if (list_values.length && list_values[list_values.length - 1] === undefined) {
              return;
            }
            onChangeMultiple?.({ document: docItem, newValue: undefined, valueAction: "add", valueIndex: undefined });
          }}
          sx={{
            cursor: list_values.length && list_values[list_values.length - 1] === undefined ? "not-allowed" : "pointer",
            opacity: list_values.length && list_values[list_values.length - 1] === undefined ? 0.5 : 1,
          }}
        >
          <Typography variant="body1" fontWeight={400}>
            + Agregar {(docItem.name || "").toLowerCase()}
          </Typography>
        </Link>
      </Box>

      <Divider sx={{ backgroundColor: "#A3D4E8", mt: 2 }} />
    </Box>
  );
};

const UploadSingleDocument = ({
  inputPrefix,
  docItem,
  onChange,
  documentIndex,
  uploadService,
  RFC,
}: InputUploadDocumentProps) => {
  return (
    <React.Fragment>
      <CustomUploadFileV2
        py={2}
        inputName={`${inputPrefix || ""}_single_document_${documentIndex}`}
        optionalName={docItem.name}
        optionalDescription={docItem.label}
        disabled={docItem.disabled}
        uploadedFile={docItem.file_value}
        setUploadedFile={(newValue) => {
          onChange?.({ document: docItem, newValue });
        }}
        metadata={{
          fileCode: docItem.file_category_code,
          uploadType: docItem.upload_type || "client",
          clientDetailsId: docItem.client_details_id,
          personId: docItem.person_id,
          guaranteeId: docItem.guarantee_id,
        }}
        required={docItem.required}
        uploadService={uploadService || "default"}
        validation_type={docItem.validation_type}
        specific_validations={docItem.specific_validations}
        RFC={RFC}
      />
    </React.Fragment>
  );
};

interface DocumentsTabBofuProps {
  inputPrefix?: string;
  tabCode: BofuTabCode;
  documents: BofuTabDocument[];
  onChange?: (params: { newValue: DocumentResponse | undefined; document: BofuTabDocument }) => void;
  onChangeMultiple?: (params: {
    newValue: DocumentResponse | undefined;
    document: BofuTabDocument;
    valueAction?: ValueAction;
    valueIndex?: number;
  }) => void;
  uploadService?: "default" | "second_guarantee";
  agregarNuevo?: () => void;
  RFC?: string;
  emptyMessage?: string;
}

const DocsEmpresa = (props: DocumentsTabBofuProps) => {
  return (
    <Box>
      {props.documents.length === 0 && (
        <React.Fragment>
          {props.emptyMessage ? (
            <Box mt={4} mb={3}>
              <Typography textAlign={"center"} fontSize={"body1"} fontWeight={400}>
                {props.emptyMessage}
              </Typography>
            </Box>
          ) : (
            <Box mt={4} mb={3}>
              <Typography textAlign={"center"} fontSize={"body1"} fontWeight={400}>
                No se encontraron documentos para esta sección
              </Typography>

              <Typography textAlign={"center"} fontSize={"body2"}>
                Por favor contacta a tu asesor para obtener más información
              </Typography>
            </Box>
          )}
        </React.Fragment>
      )}

      {props.documents.map((docItem, index) => (
        <React.Fragment key={docItem.name}>
          {docItem.multiple ? (
            <UploadMultipleDocument
              key={docItem.name}
              inputPrefix={props.inputPrefix}
              docItem={docItem}
              onChangeMultiple={props.onChangeMultiple}
              documentIndex={index}
              uploadService={props.uploadService}
              RFC={props.RFC}
            />
          ) : (
            <UploadSingleDocument
              key={docItem.name}
              inputPrefix={props.inputPrefix}
              docItem={docItem}
              onChange={props.onChange}
              documentIndex={index}
              uploadService={props.uploadService}
              RFC={props.RFC}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default DocsEmpresa;
